import { Component, Inject, NgModule, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ButtonModule } from '../button/button.module';
import { TranslateModule } from '@ngx-translate/core';
import { SharedPipesModule } from '../../utils/shared-pipes.module';

/**
 * Modal para mostrar los detalles de una transferencia.
 * Permite visualizar información, cerrar el modal y repetir la transferencia.
 *
 * @remarks
 * Este componente es standalone e importa módulos para traducción, botones, funcionalidades comunes y pipes compartidos.
 * Además, utiliza DecimalPipe para formateo numérico.
 *
 * @example
 * ```html
 * <app-transfer-details-modal [data]="transferData"></app-transfer-details-modal>
 * ```
 */
@Component({
  selector: 'app-transfer-details-modal',
  templateUrl: './transfer-details-modal.component.html',
  standalone: true,
  imports: [
    TranslateModule,
    ButtonModule,
    CommonModule,
    SharedPipesModule
  ],
  providers: [DecimalPipe]
})
export class TransferDetailsModalComponent {
  /** Router inyectado para navegación programática */
  router = inject(Router);

  /**
   * Constructor del modal.
   * @param dialog Referencia al diálogo activo para control de cierre.
   * @param data Datos inyectados con la información de la transferencia.
   */
  constructor(
    public dialog: MatDialogRef<TransferDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  /**
   * Cierra el modal, devolviendo `false`.
   */
  close(): void {
    this.dialog.close(false);
  }

  /**
   * Devuelve el nombre completo combinando los campos firstName y lastName.
   * @param fields Array de objetos con nombre y valor.
   * @returns Nombre completo concatenado.
   */
  getFullName(fields: { name: string, value: string }[]): string {
    const firstName = fields.find(f => f.name === 'firstName')?.value || '';
    const lastName = fields.find(f => f.name === 'lastName')?.value || '';
    return `${firstName} ${lastName}`;
  }

  /**
   * Guarda la información de la transacción en sessionStorage,
   * cierra el modal y navega a la pantalla de transferencias
   * para reutilizar los datos y repetir la operación.
   */
  repeatTransaction(): void {
    console.log(this.data);
    sessionStorage.setItem('transactionData', JSON.stringify(this.data));
    this.close();
    this.router.navigate(['/transfers'], { state: { reuseTransaction: true } });
  }
}
